import type { SeatKind } from './model';

export type NoticeOnFile = {
  id: string;
  lienor: string;
  sender: SeatKind;
  delivered: string;
  descriptionOfWork: string;
  contractedWith: string;
};

export const NOTICES_ON_FILE: NoticeOnFile[] = [
  {
    id: 'noo-bay-millwork',
    lienor: 'Bay Millwork',
    sender: 'carpenter',
    delivered: 'August 12, 2026',
    descriptionOfWork: 'Carpentry and millwork under the interior package',
    contractedWith: 'Palm Interiors LLC',
  },
  {
    id: 'noo-gce',
    lienor: 'Gulf Coast Electric',
    sender: 'electrician',
    delivered: 'August 18, 2026',
    descriptionOfWork: 'Electrical labor only under the interior package',
    contractedWith: 'Palm Interiors LLC',
  },
  {
    id: 'noo-ridge-line',
    lienor: 'Ridge Line Roofing',
    sender: 'roofer',
    delivered: 'August 22, 2026',
    descriptionOfWork: 'Roofing package, materials purchased from ABC Supply',
    contractedWith: 'Harbor & Palm Construction',
  },
  {
    id: 'noo-abc',
    lienor: 'ABC Supply',
    sender: 'abc',
    delivered: 'August 25, 2026',
    descriptionOfWork: 'Roofing materials delivered to the lot August 21, 2026',
    contractedWith: 'Ridge Line Roofing',
  },
];

const FILE_WIDE: SeatKind[] = ['homeowner', 'flipper', 'prime', 'title', 'attorney'];

export function visibleNotices(seat: SeatKind): NoticeOnFile[] {
  if (seat === 'agent') return [];
  if (FILE_WIDE.includes(seat)) return NOTICES_ON_FILE;
  if (seat === 'nested_gc') return NOTICES_ON_FILE.filter((n) => n.sender === 'carpenter' || n.sender === 'electrician');
  return NOTICES_ON_FILE.filter((n) => n.sender === seat);
}

export function seatSeesAffidavit(seat: SeatKind): boolean {
  return seat === 'homeowner' || seat === 'flipper' || seat === 'prime' || seat === 'title' || seat === 'attorney';
}

export type HandoffRow = [string, string];

export function seatHandoff(seat: SeatKind, abcUnpaid: boolean): { title: string; sub: string; rows: HandoffRow[] } | null {
  if (seat === 'carpenter') {
    return {
      title: 'Bay Millwork — carpenter sub',
      sub: 'Hired by Palm Interiors. NTO delivered Aug 12.',
      rows: [
        ['Role on 1412', 'Carpenter sub · interior package'],
        ['Hiring relationship', 'Palm Interiors → Harbor & Palm → owner'],
        ['Join record', 'Invited by Palm Interiors on this folio'],
        ['Notice on file', 'Delivered Aug 12'],
        ['Release record', 'Conditional partial on file · no final waiver recorded'],
        ['Whole-house collision', abcUnpaid
          ? 'ABC unpaid under Ridge Line · prime final affidavit stays listed'
          : 'ABC payment recorded under the roof; your release record is unchanged'],
        ['Your next handoff', 'Review your release with Palm Interiors; the roof supplier is handled by Ridge Line'],
      ],
    };
  }
  if (seat === 'electrician') {
    return {
      title: 'Gulf Coast Electric',
      sub: 'Labor only. NTO delivered Aug 18. No supplier on your ticket.',
      rows: [
        ['Role on 1412', 'Electrical sub · labor only'],
        ['Hiring relationship', 'Palm Interiors → Harbor & Palm → owner'],
        ['Join record', 'Invited by Palm Interiors on this folio'],
        ['Notice on file', 'Delivered Aug 18'],
        ['Release record', 'No final waiver recorded · no supplier dependency listed'],
        ['Whole-house collision', abcUnpaid
          ? 'ABC unpaid under Ridge Line · prime final affidavit stays listed'
          : 'ABC payment recorded under the roof; your release record is unchanged'],
        ['Your next handoff', 'Review your release with Palm Interiors; the roof supplier is handled by Ridge Line'],
      ],
    };
  }
  if (seat === 'nested_gc') {
    return {
      title: 'Palm Interiors — GC under the prime',
      sub: 'Hired by Harbor & Palm. Carpenter + electrician under you.',
      rows: [
        ['Your hiring party', 'Harbor & Palm · construction invitation on this folio'],
        ['Your next handoff', abcUnpaid
          ? 'Collect Bay Millwork / GCE releases. ABC under Ridge Line is not yours to clear.'
          : 'Collect remaining interior releases; roof supplier is recorded paid.'],
      ],
    };
  }
  if (seat === 'roofer') {
    return {
      title: 'Ridge Line Roofing',
      sub: 'Hired by prime. ABC materials delivered to this lot Aug 21.',
      rows: [
        ['Hiring party', 'Harbor & Palm · roofing on this job'],
        ['Your notice', 'Delivered Aug 22 · on this folio'],
        ['Your next handoff', abcUnpaid
          ? 'ABC payment record before an unconditional final. Own PDF is allowed, not LG Verified.'
          : 'Verify ABC payment, or attach your own PDF (not LG Verified).'],
      ],
    };
  }
  if (seat === 'abc') {
    return {
      title: 'ABC Supply — distribution',
      sub: 'Delivered Aug 21 · GPS at shutter on this folio. Pickup would not open this door.',
      rows: [
        ['Customer on this job', 'Ridge Line Roofing · invited delivery feed'],
        ['Notice on file', 'Aug 25 · delivered to owner and lender'],
        ['Your next handoff', abcUnpaid
          ? 'Open credit is unpaid. This ticket stays on the house until payment is recorded.'
          : 'Payment recorded. Ridge Line still must verify before an LG Verified final.'],
      ],
    };
  }
  if (seat === 'homeowner') {
    return {
      title: 'Occupying watch — this house',
      sub: 'You hired Harbor & Palm. Notices from crews you never hired still hit this lot.',
      rows: [
        ['Who you hired', 'Harbor & Palm — prime. Final on hold while ABC is open.'],
        ['Notices that hit this house', 'Bay Millwork, Gulf Coast Electric, Ridge Line, ABC. You never hired the first two.'],
        ['Your next handoff', abcUnpaid
          ? 'Do not pay Harbor final. Open the notices. ABC is still unpaid under the roof.'
          : 'Review remaining releases, then decide Harbor’s final. Title still needs your tap.'],
      ],
    };
  }
  if (seat === 'prime') {
    return {
      title: 'Harbor & Palm — prime',
      sub: 'Direct contract with the trust. Privity. No NTO from you.',
      rows: [
        ['Your next handoff', abcUnpaid
          ? 'You cannot swear a clean affidavit. ABC is Ridge Line’s supplier, not yours to mark paid.'
          : 'Review remaining releases. Listed exceptions are gone; the oath is still unsigned.'],
      ],
    };
  }
  return null;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function noticeHtml(n: NoticeOnFile): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/><title>Notice to Owner — ${esc(n.lienor)}</title>
<style>body{font:14px/1.45 Georgia,serif;max-width:720px;margin:32px auto;color:#111;padding:0 16px}
h1{font-size:18px;text-align:center}.warn{font-size:11px;text-transform:uppercase;border:1px solid #111;padding:12px;margin-bottom:20px}
.stamp{margin-top:24px;font-size:12px;color:#555}</style></head><body>
<p><a href="#" onclick="window.print();return false">Print / Save as PDF</a> · Pretend-served · Send gone</p>
<p class="warn">WARNING! FLORIDA’S CONSTRUCTION LIEN LAW ALLOWS SOME UNPAID CONTRACTORS, SUBCONTRACTORS, AND MATERIAL SUPPLIERS TO FILE LIENS AGAINST YOUR PROPERTY EVEN IF YOU HAVE MADE PAYMENT IN FULL.</p>
<h1>NOTICE TO OWNER</h1>
<p>Date: ${esc(n.delivered)}</p>
<p>The undersigned hereby informs you that he or she has furnished or is furnishing services or materials as follows: <em>${esc(n.descriptionOfWork)}</em> under an order given by ${esc(n.contractedWith)}.</p>
<p>Florida law prescribes the serving of this notice in accordance with Section 713.06, Florida Statutes.</p>
<p><strong>Lienor:</strong> ${esc(n.lienor)}</p>
<p class="stamp">Folio A-19-29-19-000-000 · 1412 Palm Ave, Tampa, FL 33602 · Unsigned local preview. Not a live certified send.</p>
</body></html>`;
}

export function openInstrument(html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
